/**
 * Booking Confirmation Component
 * Shown after successful booking
 */

import { useState } from 'react';
import { ImageUploader } from './ImageUploader';

interface UploadedImage {
  id: string;
  previewUrl: string;
  localPreview?: string;
  uploading?: boolean;
  error?: string;
}

interface BookingConfirmationProps {
  bookingReference?: string;
  /** @deprecated Use bookingReference instead */
  confirmationNumber?: string;
  scheduledDate: string;
  scheduledTime: string;
  trackingToken: string;
  vehiclePlate?: string;
  language?: 'es' | 'en';
  showImageUpload?: boolean;
}

export function BookingConfirmation({
  bookingReference,
  confirmationNumber,
  scheduledDate,
  scheduledTime,
  trackingToken,
  vehiclePlate,
  language = 'es',
  showImageUpload = true,
}: BookingConfirmationProps) {
  const t = language === 'es' ? translations.es : translations.en;
  const [images, setImages] = useState<UploadedImage[]>([]);

  // Use bookingReference if available, fall back to confirmationNumber
  const displayReference = bookingReference || confirmationNumber;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Tracking URL served from API at /track
  const trackingUrl = displayReference && vehiclePlate
    ? `${window.location.origin}/track?plate=${encodeURIComponent(vehiclePlate)}&ref=${encodeURIComponent(displayReference)}`
    : `${window.location.origin}/track?plate=${encodeURIComponent(vehiclePlate || '')}`;

  return (
    <div className="text-center space-y-4">
      {/* Success icon */}
      <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
        <svg
          className="w-6 h-6 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900">{t.title}</h3>
        <p className="text-gray-500 text-xs mt-0.5">{t.subtitle}</p>
      </div>

      {/* Confirmation details */}
      <div className="bg-gray-50 rounded-md p-3 space-y-2">
        {displayReference && (
          <div>
            <div className="text-xs text-gray-500">{t.bookingReference}</div>
            <div className="text-lg font-mono font-bold text-blue-600 tracking-wide">{displayReference}</div>
            <p className="text-[10px] text-gray-400 mt-0.5">{t.saveReference}</p>
          </div>
        )}

        <div>
          <div className="text-xs text-gray-500">{t.dateTime}</div>
          <div className="font-medium text-sm text-gray-900">
            {formatDate(scheduledDate)} - {scheduledTime}
          </div>
        </div>
      </div>

      {/* Next steps */}
      <div className="text-left bg-blue-50 rounded-md p-3 space-y-1.5">
        <h4 className="font-medium text-xs text-blue-900">{t.nextSteps}</h4>
        <ul className="text-xs text-blue-800 space-y-0.5">
          <li>• {t.step1}</li>
          <li>• {t.step2}</li>
          <li>• {t.step3}</li>
        </ul>
      </div>

      {/* Image upload section */}
      {showImageUpload && trackingToken && (
        <div className="text-left bg-gray-50 rounded-md p-3 space-y-2">
          <h4 className="font-medium text-xs text-gray-900">{t.uploadImages}</h4>
          <p className="text-xs text-gray-600">{t.uploadImagesDesc}</p>
          <ImageUploader
            trackingToken={trackingToken}
            images={images}
            onImagesChange={setImages}
          />
        </div>
      )}

      {/* Tracking link */}
      <div className="text-xs text-gray-500">
        <p>{t.trackingInfo}</p>
        <a
          href={trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline break-all"
        >
          {trackingUrl}
        </a>
      </div>
    </div>
  );
}

const translations = {
  es: {
    title: 'Cita confirmada',
    subtitle: 'Hemos recibido tu solicitud de cita',
    bookingReference: 'Referencia de la cita',
    saveReference: 'Guarda esta referencia para consultar el estado de tu cita',
    dateTime: 'Fecha y hora',
    nextSteps: 'Próximos pasos',
    step1: 'Recibirás un email de confirmación en breves minutos',
    step2: 'Te llamaremos si necesitamos más información',
    step3: 'Recuerda traer la documentación del vehículo',
    trackingInfo: 'Puedes consultar el estado de tu cita en:',
    uploadImages: 'Adjuntar fotos (opcional)',
    uploadImagesDesc: 'Sube fotos del daño del vehículo para agilizar la valoración',
  },
  en: {
    title: 'Booking confirmed',
    subtitle: 'We have received your appointment request',
    bookingReference: 'Booking reference',
    saveReference: 'Save this reference to check your booking status',
    dateTime: 'Date and time',
    nextSteps: 'Next steps',
    step1: 'You will receive a confirmation email shortly',
    step2: 'We will call you if we need more information',
    step3: 'Remember to bring the vehicle documentation',
    trackingInfo: 'You can check the status of your appointment at:',
    uploadImages: 'Attach photos (optional)',
    uploadImagesDesc: 'Upload photos of the vehicle damage to speed up the assessment',
  },
};
